import React from 'react'
import Filters from './question-filters'
import QuestionCard from './question-card'
import QuestionUnlockBankInfo from './question-unlock-bank-info'
import SuggestQuestionModal from './suggest-question-modal'
import { useQuery } from '@apollo/client'
import fetchQuestions from '../queries/questions'
import Fuse from 'fuse.js'
import * as R from 'ramda'

const stages = ['Initial', 'Mid-stream', 'Wrap-up', 'Meta']

function QuestionBank() {
  const [category, setCategory] = React.useState(null)
  const [stage, setStage] = React.useState(stages[0])
  const [isBankModalOpen, setIsBankModalOpen] = React.useState(false)
  const [isQuestionModalOpen, setIsQuestionModalOpen] = React.useState(false)
  const [dropdown, setDropdown] = React.useState(false)
  const [searchText, setSearchText] = React.useState('')

  const { error, loading, data } = useQuery(fetchQuestions)

  if (loading) return <div>Loading</div>
  if (error) return <div>Error...</div>

  const visibleCategories = (data?.question_categories || []).filter(
    (category) => !category.hidden
  )

  const handleCategoryChange = (category) => {
    setCategory(category)
  }

  const handleStageChange = (stage) => {
    setStage(stage)
  }

  const handleSearchTextChange = (searchText) => [setSearchText(searchText)]

  const toggleDropdown = (e) => {
    e.stopPropagation()

    setDropdown(!dropdown)
  }

  const hideDropdown = () => {
    setDropdown(false)
  }

  const toggleBankModal = () => {
    setIsBankModalOpen(!isBankModalOpen)
  }

  const toggleQuestionModal = () => {
    setIsQuestionModalOpen(!isQuestionModalOpen)
  }

  const questionCardMap = (filteredQuestions, category) => {
    return filteredQuestions.map((q) => (
      <QuestionCard
        enabled={category.allowed}
        id={q.item.id}
        key={q.item.id}
        text={q.item.text}
        learnerType={q.item.learnerType}
        score={q.score}
      />
    ))
  }

  const questionBankUnlock = (category) => {
    if (category && category.allowed === false) {
      return <QuestionUnlockBankInfo />
    }
  }

  const filterBySearchText = (objects, searchText) => {
    if (searchText.length > 2) {
      const fuseOptions = {
        findAllMatches: true,
        includeScore: true,
        includeMatches: true,
        shouldSort: true,
        tokenize: true,
        matchAllTokens: true,
        threshold: 0.05,
        location: 0,
        distance: 500,
        maxPatternLength: 32,
        minMatchCharLength: searchText.length,
        keys: [
          { name: 'text', weight: 0.9 },
          { name: 'learnerType', weight: 0.1 },
        ],
      }

      if (searchText.toUpperCase() === 'P&A') {
        return objects
          .filter((obj) => obj.pAndA === true)
          .map((item) => ({ item: item, matches: [] }))
      }

      const fuse = new Fuse(objects, fuseOptions)
      return fuse.search(searchText)
    } else {
      return objects.map((item) => ({ item: item, matches: [] }))
    }
  }

  const renderQuestions = () => {
    const sortByPreview = R.sortWith([R.descend(R.prop('preview'))])

    if (category === null) {
      return null
    }

    if (data?.questions) {
      let questions = data.questions
        .filter((q) => q.archived !== true)
        .filter(
          (q) =>
            category !== null &&
            q.category.id === category.id &&
            q.stage === stage
        )
      if (category !== null && category.allowed === false) {
        questions = questions.filter((q) => q.preview === true)
      }
      questions = sortByPreview(questions)
      var filteredQuestions = filterBySearchText(questions, searchText).map(
        highlightMatches
      )
      if (category !== null && filteredQuestions.length === 0) {
        return <p style={{ textAlign: 'center' }}>No matching questions.</p>
      } else {
        return (
          <div>
            {questionCardMap(filteredQuestions, category)}
            {questionBankUnlock(category)}
          </div>
        )
      }
    }
  }

  const highlightMatches = (result) => {
    // if (result.score < 0.5) {
    let newResult = R.clone(result)
    // let matches = result.matches.filter((m) => m.key == 'text')

    for (let match of result.matches) {
      // Find the longest match in format [startIndex, endIndex]
      let index = match.indices.reduce(
        (prev, next) => {
          let prevLength = prev[1] - prev[0]
          let nextLength = next[1] - next[0]

          return nextLength > prevLength ? next : prev
        },
        [0, 0]
      )

      let str = newResult.item[match.key]
      newResult.item[match.key] =
        str.substring(0, index[0]) +
        '<mark>' +
        str.substring(index[0], index[1] + 1) +
        '</mark>' +
        str.substring(index[1] + 1)
    }

    return newResult
  }

  return (
    <div className="question-bank" onClick={hideDropdown}>
      <Filters
        categories={visibleCategories}
        category={category}
        stages={stages}
        stage={stage}
        dropdown={dropdown}
        toggleDropdown={toggleDropdown}
        onCategoryChange={handleCategoryChange}
        onStageChange={handleStageChange}
        searchText={searchText}
        onSearchTextChange={handleSearchTextChange}
      />
      {category === null && visibleCategories.length === 0 && (
        <p className="empty-question-banks">
          No question banks are available for this organization.
        </p>
      )}
      {renderQuestions()}
      {category === null && (
        <div className="suggest-a-question" onClick={toggleBankModal}>
          Suggest a Bank
        </div>
      )}
      <SuggestQuestionModal
        isOpen={isBankModalOpen}
        variant="bank"
        onCancel={toggleBankModal}
      />
    </div>
  )
}

export default QuestionBank
